import { InfiniteData, QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import dayjs from "dayjs";
import { api } from "../../../singleton/axios";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { useSafeNavigate } from "../../../hooks/useSafeNavigate";
import { isNative, openExternal } from "../../../lib/platform";
import { INBOX_PATH, resolveServerTarget } from "../../../lib/notificationTarget";
import {
  NotificationOpenSource,
  NotificationsPage,
  OpenNotificationResponse,
  RecentNotifications,
} from "../types";
import { RECENT_NOTIFICATIONS_QUERY_KEY } from "./useRecentNotifications";
import { NOTIFICATIONS_INBOX_QUERY_KEY } from "./useNotifications";
import {
  NOTIFICATION_UNAVAILABLE,
  isNotificationUnavailableError,
  notificationItemQueryKey,
  notificationLinkPath,
} from "../notificationUnavailable";
import { COMPANY_INSIDER_TRADING_QUERY_KEY } from "../../Dashboard/Charts/hooks/useCompanyInsiderTrading";

interface OpenNotificationVariables {
  id: string;
  source: NotificationOpenSource;
  /**
   * Replace the current history entry. Set by the /n/:id link route, which is
   * a waypoint the back button should never return to.
   */
  replace?: boolean;
}

type InboxData = InfiniteData<NotificationsPage, string | null>;

const DUMMY_ORIGIN = "https://internal.invalid";

/**
 * The insider hook caches for 2.5 minutes with an 8-hour gcTime, so without
 * this the Insiders tab would open WITHOUT the trades the alert is about. The
 * ticker comes from the path the server just resolved, never from the cached
 * item: tickers change hands. Every period of that ticker's company-level
 * query is invalidated — the tab may open on any of them.
 */
const invalidateDestinationQueries = (queryClient: QueryClient, path: string): void => {
  const url = new URL(path, DUMMY_ORIGIN);
  if (url.pathname !== "/charts") return;
  const ticker = url.searchParams.get("ticker")?.toUpperCase();
  if (!ticker) return;
  void queryClient.invalidateQueries({
    predicate: ({ queryKey }) =>
      queryKey[0] === COMPANY_INSIDER_TRADING_QUERY_KEY &&
      String(queryKey[1]).toUpperCase() === ticker,
  });
};

/**
 * THE ONLY way a notification is opened — the row tap, the emailed link and
 * the push tap all come through here. It marks the item read with an
 * authenticated POST and navigates on the `target` the server resolved at that
 * moment, never on the `path` cached in the list, then re-checks that target
 * with this binary's own guard. Anything the guard refuses opens the inbox,
 * where the row is visible — an older binary must never no-op.
 *
 * Mount it somewhere that OUTLIVES the navigation (NotificationsProvider):
 * useSafeNavigate's watchdog reads a ref its owner keeps current, and an owner
 * that unmounts on success looks like a dead router and triggers a full reload.
 */
export const useOpenNotification = () => {
  const queryClient = useQueryClient();
  const navigate = useSafeNavigate();
  const { capture } = useAnalytics();

  return useMutation({
    mutationFn: async ({ id }: OpenNotificationVariables) => {
      // `{}`: the route requires a JSON body (cross-site form protection).
      const { data } = await api.post<OpenNotificationResponse>(
        `/notifications/${encodeURIComponent(id)}/read`,
        {},
      );
      return data;
    },

    // Opening moves an item out of Recent and clears its unread dot. Do both
    // now; the server confirms on settle.
    onMutate: async ({ id }) => {
      // As in useClearRecent: an inbox read in flight would undo the dot.
      await Promise.all([
        queryClient.cancelQueries({ queryKey: RECENT_NOTIFICATIONS_QUERY_KEY }),
        queryClient.cancelQueries({ queryKey: NOTIFICATIONS_INBOX_QUERY_KEY }),
      ]);

      const previousRecent = queryClient.getQueryData<RecentNotifications>(
        RECENT_NOTIFICATIONS_QUERY_KEY,
      );
      const previousInbox = queryClient.getQueryData<InboxData>(NOTIFICATIONS_INBOX_QUERY_KEY);

      queryClient.setQueryData<RecentNotifications>(RECENT_NOTIFICATIONS_QUERY_KEY, (old) => {
        if (!old || !old.items.some((item) => item.id === id)) return old;
        return {
          items: old.items.filter((item) => item.id !== id),
          count: Math.max(old.count - 1, 0),
        };
      });

      const readAt = dayjs().toISOString();
      queryClient.setQueryData<InboxData>(NOTIFICATIONS_INBOX_QUERY_KEY, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((item) =>
              item.id === id && item.readAt === null ? { ...item, readAt, recent: false } : item,
            ),
          })),
        };
      });

      return { previousRecent, previousInbox };
    },

    onSuccess: ({ item, target }, { source, replace = false }) => {
      capture("notification_opened", { type: item.type, source });

      const resolved = resolveServerTarget(target, { isNative: isNative() });

      if (resolved.kind === "external") {
        void openExternal(resolved.url);
        // The link route has nothing of its own to show once the sheet opens.
        if (replace) navigate(INBOX_PATH, { replace: true });
        return;
      }

      const path = resolved.kind === "internal" ? resolved.path : INBOX_PATH;
      invalidateDestinationQueries(queryClient, path);

      // Already looking at the inbox: the row is in view, nothing to push.
      if (path === INBOX_PATH && window.location.pathname === INBOX_PATH) return;
      navigate(path, { replace });
    },

    onError: (error, { id, source, replace = false }, context) => {
      // Withdrawn (410): it has left Recent and the history for good, so the
      // optimistic removal stands, and it opens only on the neutral "no longer
      // available" page — never a silent redirect, never an error toast.
      if (isNotificationUnavailableError(error)) {
        queryClient.setQueryData<InboxData>(NOTIFICATIONS_INBOX_QUERY_KEY, (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.filter((item) => item.id !== id),
            })),
          };
        });
        queryClient.setQueryData(notificationItemQueryKey(id), NOTIFICATION_UNAVAILABLE);
        const linkPath = notificationLinkPath(id);
        if (window.location.pathname !== linkPath) navigate(linkPath, { replace });
        return;
      }
      if (context?.previousRecent) {
        queryClient.setQueryData(RECENT_NOTIFICATIONS_QUERY_KEY, context.previousRecent);
      }
      if (context?.previousInbox) {
        queryClient.setQueryData(NOTIFICATIONS_INBOX_QUERY_KEY, context.previousInbox);
      }
      // A link that cannot be opened (pruned, someone else's, offline) still
      // has to land somewhere.
      if (replace) {
        navigate(INBOX_PATH, { replace: true });
        return;
      }
      // So does a push tap: the user arrived from outside the app, so a toast
      // over whatever screen happened to be open would explain nothing.
      if (source === "push") {
        if (window.location.pathname !== INBOX_PATH) navigate(INBOX_PATH);
        return;
      }
      enqueueSnackbar("Couldn't open that notification. Please try again.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: RECENT_NOTIFICATIONS_QUERY_KEY });
      // Mark the history stale without refetching every loaded page now — the
      // optimistic dot is already right, and the next visit refetches.
      void queryClient.invalidateQueries({
        queryKey: NOTIFICATIONS_INBOX_QUERY_KEY,
        refetchType: "none",
      });
    },
  });
};
